import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, ArrowLeft, FileDown, Eye, Percent, Check, Layers, Briefcase, X, Ban,
} from "lucide-react";
import {
  getToken, fetchQuote, openQuotePdf, previewVatOverride, confirmVatOverride, formatEuro,
  confirmQuote, cancelQuote,
} from "@/lib/mastroAuth";
import { StatusBadge } from "@/components/StatusBadge";
import { QUOTES } from "@/constants/testIds";

const Section = ({ title, icon: Icon, testid, children }) => (
  <section className="mt-6" data-testid={testid}>
    <div className="mb-2 flex items-center gap-2">
      {Icon && <Icon className="h-4 w-4 text-neutral-400" />}
      <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">{title}</h2>
    </div>
    {children}
  </section>
);

export default function QuoteDetail() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // VAT override panel
  const [showVat, setShowVat] = useState(false);
  const [applied, setApplied] = useState(true);
  const [rate, setRate] = useState("22");
  const [mode, setMode] = useState("excluded");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = () =>
    fetchQuote(getToken(), quoteId)
      .then(setData)
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, [quoteId]);

  if (loading) return <div className="flex items-center gap-3 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /> Caricamento…</div>;
  if (notFound || !data) return (
    <div>
      <button onClick={() => navigate("/app/preventivi")} className="mb-4 inline-flex items-center gap-1 text-sm text-neutral-600 hover:underline"><ArrowLeft className="h-4 w-4" /> Preventivi</button>
      <p className="rounded-xl border border-neutral-200 bg-white p-6 text-sm text-neutral-500">Preventivo non trovato.</p>
    </div>
  );

  const { quote, current, versions } = data;
  const token = getToken();

  const doPreview = async () => {
    setBusy(true); setErr(null); setPreview(null);
    try {
      const res = await previewVatOverride(token, quoteId, { applied, rate, mode });
      setPreview(res.projected);
    } catch (e) { setErr("Anteprima non riuscita."); } finally { setBusy(false); }
  };
  const doConfirm = async () => {
    setBusy(true); setErr(null);
    try {
      const updated = await confirmVatOverride(token, quoteId, { applied, rate, mode });
      setData(updated); setPreview(null); setShowVat(false);
    } catch (e) { setErr("Conferma non riuscita."); } finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} data-testid={QUOTES.detail}>
      <button data-testid={QUOTES.back} onClick={() => navigate("/app/preventivi")} className="mb-4 inline-flex items-center gap-1 text-sm text-neutral-600 hover:underline"><ArrowLeft className="h-4 w-4" /> Preventivi</button>

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">{quote.job_title || "Preventivo"}</h1>
        <StatusBadge status={quote.status} />
      </div>
      {quote.job_id && (
        <button data-testid={QUOTES.jobLink} onClick={() => navigate(`/app/lavori/${quote.job_id}`)}
          className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-teal-700 hover:underline">
          <Briefcase className="h-4 w-4" /> Vai al lavoro collegato
        </button>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <button data-testid={QUOTES.pdfPreview} onClick={() => openQuotePdf(token, quoteId, quote.current_version, false)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50">
          <Eye className="h-4 w-4" /> Anteprima PDF
        </button>
        <button data-testid={QUOTES.pdfDownload} onClick={() => openQuotePdf(token, quoteId, quote.current_version, true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50">
          <FileDown className="h-4 w-4" /> Scarica PDF
        </button>
        {quote.status === "DRAFT" && (
          <button data-testid={QUOTES.confirm}
            onClick={async () => { const d = await confirmQuote(token, quoteId); setData(d); }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-teal-700">
            <Check className="h-4 w-4" /> Conferma
          </button>
        )}
        {(quote.status === "DRAFT" || quote.status === "CONFIRMED") && (
          <button data-testid={QUOTES.cancel}
            onClick={async () => { const d = await cancelQuote(token, quoteId); setData(d); }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50">
            <Ban className="h-4 w-4" /> Annulla
          </button>
        )}
      </div>

      <Section title={`Versione corrente (v${quote.current_version})`}>
        <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
          {current?.lines?.map((ln, i) => (
            <div key={i} className="flex items-center justify-between border-b border-neutral-100 px-3 py-2 text-sm last:border-0">
              <span className="text-neutral-800">{ln.description || "—"}</span>
              <span className="text-neutral-500">{ln.quantity ?? "—"} {ln.unit || ""} · {formatEuro(ln.unit_price)} · <b className="text-neutral-800">{formatEuro(ln.line_total)}</b></span>
            </div>
          ))}
          <div className="px-3 py-2 text-sm">
            <div className="flex justify-between"><span className="text-neutral-500">Imponibile</span><span>{formatEuro(current?.imponibile)}</span></div>
            {current?.vat_applied && (
              <div className="mt-1 flex justify-between"><span className="text-neutral-500">IVA {current?.vat_rate}% ({current?.pricing_mode === "included" ? "inclusa" : "esclusa"})</span><span>{formatEuro(current?.vat_amount)}</span></div>
            )}
            <div className="mt-2 flex justify-between border-t border-neutral-100 pt-2 font-semibold"><span>Totale</span><span>{formatEuro(current?.total)}</span></div>
          </div>
        </div>
      </Section>

      {/* VAT override (explicit, per-quote) */}
      <Section title="Trattamento IVA di questo preventivo" icon={Percent}>
        <p className="text-xs text-neutral-500">Modifica l'IVA solo di questo preventivo (non la configurazione predefinita del workspace). Genererà una nuova versione.</p>
        {!showVat ? (
          <button data-testid={QUOTES.vatToggle} onClick={() => setShowVat(true)}
            className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50">
            <Percent className="h-4 w-4" /> Modifica IVA del preventivo
          </button>
        ) : (
          <div className="mt-3 rounded-xl border border-neutral-200 bg-white p-5">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <label className="flex items-center gap-2 text-sm">
                <input data-testid={QUOTES.vatApplied} type="checkbox" checked={applied} onChange={(e) => setApplied(e.target.checked)} className="accent-teal-600" /> IVA applicata
              </label>
              <label className="flex items-center gap-2 text-sm">
                Aliquota %
                <input data-testid={QUOTES.vatRate} type="number" value={rate} onChange={(e) => setRate(e.target.value)} disabled={!applied}
                  className="w-20 rounded border border-neutral-300 px-2 py-1 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 disabled:bg-neutral-100" />
              </label>
              <label className="flex items-center gap-2 text-sm">
                Prezzi
                <select data-testid={QUOTES.vatMode} value={mode} onChange={(e) => setMode(e.target.value)} disabled={!applied}
                  className="rounded border border-neutral-300 px-2 py-1 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 disabled:bg-neutral-100">
                  <option value="excluded">IVA esclusa</option>
                  <option value="included">IVA inclusa</option>
                </select>
              </label>
            </div>
            {err && <p className="mt-3 text-sm text-red-600">{err}</p>}
            <div className="mt-4 flex flex-wrap gap-2 border-t border-neutral-100 pt-4">
              <button data-testid={QUOTES.vatPreview} onClick={doPreview} disabled={busy}
                className="inline-flex items-center gap-1 rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm font-medium disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Anteprima calcolo
              </button>
              <button data-testid={QUOTES.vatConfirm} onClick={doConfirm} disabled={busy || !preview}
                className="inline-flex items-center gap-1 rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-teal-700 disabled:opacity-60">
                <Check className="h-4 w-4" /> Conferma e crea nuova versione
              </button>
              <button onClick={() => { setShowVat(false); setPreview(null); }} className="px-2 py-1.5 text-sm text-neutral-500 hover:text-neutral-800">Annulla</button>
            </div>
            {preview && (
              <div data-testid={QUOTES.vatPreviewResult} className="mt-4 rounded-lg bg-neutral-50 px-4 py-3 text-sm">
                <div className="mb-1 text-xs font-medium uppercase text-neutral-400">Anteprima (calcolo deterministico, non ancora salvato)</div>
                <div className="flex justify-between"><span className="text-neutral-500">Imponibile</span><span>{formatEuro(preview.imponibile)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">IVA</span><span>{formatEuro(preview.vat_amount)}</span></div>
                <div className="flex justify-between font-semibold"><span>Totale</span><span>{formatEuro(preview.total)}</span></div>
              </div>
            )}
          </div>
        )}
      </Section>

      <Section title="Versioni" icon={Layers} testid={QUOTES.versions}>
        <div className="space-y-2">
          {versions.map((v) => (
            <div key={v.version_number} data-testid={QUOTES.versionItem}
              className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${v.is_current ? "border-teal-300 bg-teal-50/40" : "border-neutral-200 bg-white"}`}>
              <div>
                <span className="font-medium text-neutral-950">v{v.version_number}</span>
                {v.is_current && <span className="ml-2 rounded-full bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-700">corrente</span>}
                <span className="ml-2 text-xs text-neutral-500">{v.vat_applied ? `IVA ${v.vat_rate}%` : "senza IVA"} · {v.vat_source}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-semibold">{formatEuro(v.total)}</span>
                <button
                  onClick={() => openQuotePdf(token, quoteId, v.version_number, false)}
                  aria-label={`Apri PDF versione ${v.version_number}`}
                  className="inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-white px-2 py-1 text-xs font-medium text-teal-700 hover:border-teal-300 hover:bg-teal-50"
                >
                  <Eye className="h-3.5 w-3.5" /> Apri PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </motion.div>
  );
}
